import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useAuthStore } from '../../stores/authStore'
import { Eye, EyeOff, Loader2, User } from 'lucide-react'
import toast from 'react-hot-toast'
import './LoginPage.css'

export default function LoginPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { login, isLoggingIn, error, clearError } = useAuthStore()
    const [showPassword, setShowPassword] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        defaultValues: {
            login: '',
            password: ''
        }
    })

    const onSubmit = async (data) => {
        clearError()
        try {
            const result = await login(data.login, data.password)
            if (result.success) {
                navigate('/dashboard')
            } else {
                // Show toast error with translated message based on error code
                const errorMessage = t(`auth.errors.${result.code}`, t('auth.errors.LOGIN_FAILED'))
                toast.error(errorMessage, { id: 'login-error' })
            }
        } catch (err) {
            toast.error(t('auth.errors.LOGIN_FAILED'), { id: 'login-error' })
        }
    }

    return (
        <div className="login-page">
            <h2 className="login-title">{t('auth.loginTitle')}</h2>
            <p className="login-subtitle">{t('auth.loginSubtitle')}</p>

            {error && (
                <div className="login-error">
                    {t(`auth.errors.${error}`, t('auth.errors.LOGIN_FAILED'))}
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="login-form" noValidate>
                <div className="form-group">
                    <label className="form-label" htmlFor="login">
                        {t('auth.login')}
                    </label>
                    <div className="login-input-wrapper">
                        <input
                            id="login"
                            type="text"
                            className={`form-input ${errors.login ? 'error' : ''}`}
                            placeholder={t('auth.loginPlaceholder')}
                            autoComplete="username"
                            {...register('login', {
                                required: t('validation.required'),
                                minLength: {
                                    value: 3,
                                    message: t('validation.minLength', { min: 3 })
                                }
                            })}
                        />
                        <User size={18} className="input-icon" />
                    </div>
                    {errors.login && (
                        <span className="form-error">{errors.login.message}</span>
                    )}
                </div>

                <div className="form-group">
                    <label className="form-label" htmlFor="password">
                        {t('auth.password')}
                    </label>
                    <div className="password-input-wrapper">
                        <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            className={`form-input ${errors.password ? 'error' : ''}`}
                            placeholder="••••••••"
                            autoComplete="current-password"
                            {...register('password', {
                                required: t('validation.required')
                            })}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex={-1}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {errors.password && (
                        <span className="form-error">{errors.password.message}</span>
                    )}
                </div>

                <button
                    type="submit"
                    className="btn btn-primary btn-lg w-full"
                    disabled={isLoggingIn}
                >
                    {isLoggingIn ? (
                        <>
                            <Loader2 size={18} className="spin" />
                            {t('common.loading')}
                        </>
                    ) : (
                        t('auth.loginButton')
                    )}
                </button>
            </form>
        </div>
    )
}
